/**
 * Der Katalog der Kundenarten.
 *
 * Vorher stand diese Liste als feste Aufzählung im Programm: Gemeinde,
 * Kita, Schule, Kirche, Wohnungswirtschaft, Privat, Sonstige. Das ging so
 * lange gut, wie die Kunden Gemeinden und Kitas waren — aber ein Prüfbüro
 * arbeitet auch für Hersteller, für Wohnungsunternehmen, für Betreiber im
 * Auftrag Dritter. Jede neue Art hätte eine Migration gebraucht, und das
 * ist der falsche Preis für ein Wort in einer Liste.
 *
 * Löschen ist hier ungefährlich: am Kunden steht der Name als Text, kein
 * Verweis. Wer eine Art entfernt, nimmt sie aus der Auswahl — die Kunden,
 * die sie tragen, behalten sie.
 */

import { useState } from 'react'
import {
  useKundenartAendern,
  useKundenartAnlegen,
  useKundenartLoeschen,
  useKundenarten,
} from '../api/abfragen'
import { Fehlerhinweis, Ladeanzeige } from './Hinweise'

export function Kundenarten() {
  // Auch die stillgelegten: die Pflege muss sie sehen, um sie wieder in
  // Betrieb nehmen zu können.
  const arten = useKundenarten(false)
  const anlegen = useKundenartAnlegen()
  const aendern = useKundenartAendern()
  const loeschen = useKundenartLoeschen()
  const [neu, setzeNeu] = useState('')

  if (arten.isLoading) return <Ladeanzeige />
  if (arten.error) return <Fehlerhinweis fehler={arten.error} />

  const liste = arten.data ?? []

  async function verschieben(index: number, richtung: -1 | 1) {
    const ziel = index + richtung
    if (ziel < 0 || ziel >= liste.length) return

    const neuOrdnung = [...liste]
    const [bewegt] = neuOrdnung.splice(index, 1)
    neuOrdnung.splice(ziel, 0, bewegt)

    // Alle neu vergeben statt zwei zu tauschen: ein Tausch tut nichts,
    // wenn zwei Einträge dieselbe Sortierung tragen.
    for (const [i, art] of neuOrdnung.entries()) {
      const sortierung = (i + 1) * 10
      if (art.sortierung === sortierung) continue
      await aendern.mutateAsync({ id: art.id, sortierung })
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-bold">Kundenarten</h2>
        <p className="mt-1 text-base text-slate-700">
          Wonach Sie Ihre Kunden unterscheiden. Die Reihenfolge ist die der
          Auswahlliste beim Kunden.
        </p>
      </div>

      <Fehlerhinweis fehler={anlegen.error} />
      <Fehlerhinweis fehler={aendern.error} />
      <Fehlerhinweis fehler={loeschen.error} />

      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault()
          const name = neu.trim()
          if (!name) return
          anlegen.mutate({ name }, { onSuccess: () => setzeNeu('') })
        }}
      >
        <div className="flex-1">
          <label htmlFor="ka-neu">Neue Art</label>
          <input
            id="ka-neu"
            value={neu}
            onChange={(e) => setzeNeu(e.target.value)}
            maxLength={80}
            placeholder="Hersteller"
          />
        </div>
        <button
          type="submit"
          className="knopf-zweit px-3"
          disabled={!neu.trim() || anlegen.isPending}
        >
          Hinzufügen
        </button>
      </form>

      <ol className="space-y-2">
        {liste.map((a, i) => (
          <li
            key={a.id}
            className={`karte flex flex-wrap items-center gap-2 ${
              a.aktiv ? '' : 'border-dashed opacity-60'
            }`}
          >
            <div className="min-w-0 flex-1">
              <span className="font-semibold">{a.name}</span>
              {!a.aktiv && (
                <span className="ml-2 text-base text-slate-600">· stillgelegt</span>
              )}
            </div>

            <button
              type="button"
              className="knopf-still px-2 text-base"
              aria-label={`„${a.name}" nach oben`}
              disabled={i === 0 || aendern.isPending}
              onClick={() => void verschieben(i, -1)}
            >
              ↑
            </button>
            <button
              type="button"
              className="knopf-still px-2 text-base"
              aria-label={`„${a.name}" nach unten`}
              disabled={i === liste.length - 1 || aendern.isPending}
              onClick={() => void verschieben(i, 1)}
            >
              ↓
            </button>
            <button
              type="button"
              className="knopf-still px-3 text-base"
              onClick={() => aendern.mutate({ id: a.id, aktiv: !a.aktiv })}
            >
              {a.aktiv ? 'Stilllegen' : 'Wieder verwenden'}
            </button>
            <button
              type="button"
              className="knopf-warnung px-3 text-base"
              disabled={loeschen.isPending}
              onClick={() => {
                const frage =
                  `Kundenart „${a.name}" aus der Auswahl entfernen?\n\n` +
                  'Kunden, die sie tragen, behalten sie — am Kunden steht der ' +
                  'Name als Text, kein Verweis. Sie steht nur nicht mehr zur Wahl.'
                if (window.confirm(frage)) loeschen.mutate(a.id)
              }}
            >
              Löschen
            </button>
          </li>
        ))}
      </ol>
    </div>
  )
}
